import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data';
import { getAGGClient, isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import { DynamicPricingQuery } from '../graphql/DynamicPricingQuery.agggraphql';

interface Prices {
	loading: boolean;
	error?: Error;
	standardPrice?: number;
	premiumPrice?: number;
}

type ChargeElementType = { item: { chargeElement: string } };
/**
 * Returns the <=100 user tier per-user-per-month prices for Standard and Premium editions of
 * Confluence.
 */
export const usePricing = (skip: boolean): Prices => {
	const PRODUCT_KEY = 'confluence.ondemand';

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();

	const {
		data,
		loading,
		error: pricingError,
	} = useQuery(DynamicPricingQuery, {
		client: getAGGClient(),
		variables: {
			cloudId,
			productKey: PRODUCT_KEY,
		},
		skip,
	});

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const standardPrice =
		entitlement?.defaultOfferingTransitions?.[0]?.listPriceForOrderWithDefaults?.find(
			(item: ChargeElementType) => item.item.chargeElement === 'user',
		)?.averageAmountPerUnit;

	const premiumPrice = entitlement?.subscription?.trial?.listPriceEstimates?.find(
		(item: ChargeElementType) => item.item.chargeElement === 'user',
	)?.averageAmountPerUnit;

	const missingPricingError =
		!loading && !skip && (!standardPrice || !premiumPrice)
			? new Error('Pricing returned empty')
			: undefined;

	const error = pricingError || missingPricingError;
	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'usePricing',
				source: 'RecommendStandardOverPremiumModal',
				attributes: {
					growthFeature: 'RecommendStandardOverPremiumModal',
					pricingError: pricingError?.message,
					missingPricingError: missingPricingError?.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	return {
		loading,
		error,
		standardPrice: formatPricing(standardPrice),
		premiumPrice: formatPricing(premiumPrice),
	};
};

const formatPricing = (unformattedPrice?: number) => {
	if (!unformattedPrice) return undefined;
	return unformattedPrice / 100;
};
